<template>
  <div>
    <div class="overlay-disable-canvas" v-if="isDisableCanvas"></div>
    <SlideTitle
      v-if="isSlideTitleComponentVisible"
      :showSoloRanking="showSoloRanking"
      @hideQuestionImage="hideQuestionImage"
    />

    <div
      class="text-center text-no-answer-correct"
      v-if="showTextNoneIsCorrect"
    >
      {{ $t('None of the options is correct!') }}
    </div>

    <!-- Canvas content -->
    <div class="flex canvas-wrapper">
      <div class="canvas-content" v-if="isOpenEndedSlide">
        <OpenEndedContent />
      </div>
      <div class="canvas-content canvas-textSlide" v-if="slideActive.type == 'textSlide'">
        <TextSlideContent />
      </div>

      <div class="canvas-content canvas-textSlide" v-if="slideActive.type == 'document'">
        <DocumentContent />
      </div>
      <div class="canvas-content canvas-bullet-points" v-if="slideActive.type == 'bulletPoints'">
        <BulletPointsContent />
      </div>

      <div class="canvas-content horizontal layout center-justified" v-if="isQrCodeSlide">
        <QRCodeContent />
      </div>
      <div
        class="canvas-content horizontal layout center-justified"
        :class="{ 'center': isSlideImageInDefaultMode }"
        v-if="isImageSlide"
      >
        <ImageContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isYouTubeSlide"
      >
        <YoutubeContent />
      </div>

      <div
        class="pb-2 canvas-content horizontal layout end center-justified"
        v-if="isGoogleSlidesContentVisible"
      >
        <GoogleSlidesContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isWordCloudSlide"
      >
        <WordCloudContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isQnASlide"
      >
        <QuestionContent />
      </div>

      <div
        class="canvas-content horizontal layout center center-justified"
        v-if="isBrainstormSlide"
      >
        <VotingIdeasContentWrapper />
      </div>
      <div class="canvas-content p-3" v-if="isPollSlide">
        <PollContentWrapper @updateLayout="updateQuestionImageLayout" />
      </div>
      <div class="canvas-content" v-if="isScalesSlide">
        <ScaleQuestion />
      </div>

      <div class="canvas-content" v-if="isPickAnswerSlideType">
        <div v-if="!isPresentingOrBackstage && !sharePresentation" class="full-size">
          <CanvasHints v-if="isCanvasHintsVisible" />
          <BarChart v-if="slideActive.SlideOptions.length" />
        </div>
        <div class="full-size" v-if="isPresentingOrBackstage && !sharePresentation">
          <QuizSlideContent />
        </div>
        <div v-if="sharePresentation" class="full-size">
          <BarChart v-if="slideActive.SlideOptions.length" />
        </div>
      </div>

      <div class="canvas-content" v-if="isPickImageSlideType">
        <div v-if="!isPresentingOrBackstage && !sharePresentation" class="full-size">
          <CanvasHints v-if="isCanvasHintsVisible" />
          <CustomBarChart v-if="slideActive.SlideOptions.length" />
        </div>
        <div class="full-size" v-if="isPresentingOrBackstage && !sharePresentation">
          <QuizSlideContent />
        </div>
        <div v-if="sharePresentation" class="full-size">
          <CustomBarChart v-if="slideActive.SlideOptions.length" />
        </div>
      </div>

      <div class="canvas-content" v-if="isTypeAnswerSlideType">
        <div class="full-size" v-if="isPresentingOrBackstage && !sharePresentation">
          <QuizSlideContent />
        </div>
        <div v-if="sharePresentation || !isPresentingOrBackstage" class="full-size">
          <CustomBarChartQuizType />
        </div>
      </div>

      <div class="canvas-content" v-if="isMatchPairsSlideType">
        <div v-if="isPresentingOrBackstage && !sharePresentation" class="full-size">
          <QuizSlideContent />
        </div>

        <div v-else class="full-size horizontal layout center center-justified">
          <MatchPairsContent />
        </div>
      </div>

      <!-- Correct Order Content -->
      <div class="canvas-content" v-if="isCorrectOrderSlideType">
        <div v-if="isPresentingOrBackstage && !sharePresentation" class="full-size">
          <QuizSlideContent />
        </div>

        <div v-else class="full-size horizontal layout center center-justified">
          <CorrectOrderContent />
        </div>
      </div>

      <div class="canvas-content" v-if="isLeaderboardSlide">
        <LeaderboardContent
          v-if="!presentation.teamPlay || showSoloRanking"
          @toggleShowCarousel="toggleShowCarousel"
          @showWinner="handleShowWinner"
          :show-carousel="showCarouselSolo"
        />
        <LeaderboardTeamContent
          v-if="presentation.teamPlay && !showSoloRanking"
          @toggleShowCarousel="toggleShowCarousel"
          @showWinner="handleShowWinner"
          :show-carousel="showCarouselTeam"
        />
      </div>

      <div class="canvas-content" v-if="isSpinnerWheelSlide">
        <SpinnerWheelCanvas />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import QuizStatuses from '@/constant/QuizStatuses'
import SlideTitle from '@/components-v2/presentation-editor/middle/SlideTitle.vue'
import OpenEndedContent from '@/components/presentations/contents/OpenEndedContent.vue'
import TextSlideContent from '@/components/presentations/contents/TextSlideContent.vue'
import BulletPointsContent from '@/components/presentations/contents/BulletPointsContent.vue'
import ImageContent from '@/components/presentations/contents/ImageContent.vue'
import QRCodeContent from '@/components/presentations/contents/QRCodeContent.vue'
import DocumentContent from '@/components/presentations/contents/DocumentContent.vue'
import GoogleSlidesContent from '@/components/presentations/contents/GoogleSlidesContent.vue'
import YoutubeContent from '@/components/presentations/contents/YoutubeContent.vue'
import WordCloudContent from '@/components/presentations/contents/WordCloudContent.vue'
import QuizSlideContent from '@/components/presentations/contents/QuizSlideContent.vue'
import LeaderboardContent from '@/components/presentations/contents/LeaderboardContent.vue'
import LeaderboardTeamContent from '@/components/presentations/contents/LeaderboardTeamContent.vue'
import QuestionContent from '@/components/presentations/contents/QuestionContent.vue'
import MatchPairsContent from '@/components-v2/presentation-editor/match-pairs/MatchPairsContent.vue'
import CorrectOrderContent from '@/components-v2/presentation-editor/correct-order/CorrectOrderContent.vue'
import VotingIdeasContentWrapper from '@/components/presentations/contents/VotingIdeasContentWrapper.vue'
import PollContentWrapper from '@/components/presentations/contents/PollContentWrapper.vue'
import SpinnerWheelCanvas from '@/components-v2/presentation-editor/spinner-wheel-canvas/SpinnerWheelCanvas.vue'
import CanvasHints from '@/components-v2/presentation-editor/middle/hints/CanvasHints.vue'

// charts
import BarChart from '@/components/presentations/charts/BarChart.vue'
import ScaleQuestion from '@/components/presentations/charts/ScaleQuestion.vue'
import CustomBarChart from '@/components/presentations/charts/CustomBarChart.vue'
import CustomBarChartQuizType from '@/components/presentations/charts/CustomBarChartQuizType.vue'

export default {
  components: {
    SlideTitle,
    OpenEndedContent,
    TextSlideContent,
    BulletPointsContent,
    ImageContent,
    QRCodeContent,
    DocumentContent,
    GoogleSlidesContent,
    YoutubeContent,
    WordCloudContent,
    QuizSlideContent,
    LeaderboardContent,
    LeaderboardTeamContent,
    QuestionContent,
    MatchPairsContent,
    CorrectOrderContent,
    VotingIdeasContentWrapper,
    PollContentWrapper,
    SpinnerWheelCanvas,
    CanvasHints,

    // charts
    BarChart,
    ScaleQuestion,
    CustomBarChart,
    CustomBarChartQuizType,
  },
  props: {
    showSoloRanking: {
      type: Boolean,
      default: false,
    },
    showCarouselSolo: {
      type: Boolean,
      default: false,
    },
    showCarouselTeam: {
      type: Boolean,
      default: false,
    },
    showWinnerId: {
      type: Number,
      default: null,
    },
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      sharePresentation: (state) => state.sharePresentation,
      isPlayDemoMode: (state) => state.isPlayDemoMode,
      isScreenCapture: (state) => state.isScreenCapture,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    isDisableCanvas() {
      return this.isDisableEditPresentation
    },
    isSlideTitleComponentVisible() {
      const slideTypes = ['googleSlides', 'textSlide', 'bulletPoints', 'freestyle']
      return !slideTypes.includes(this.slideActive.type)
    },
    hasClassHiddenAudio() {
      const { type, quizStatus } = this.slideActive
      return this.isPresenting && type == 'pickAnswer' && quizStatus < QuizStatuses.COUNTDOWN_5S_SCREEN && !this.sharePresentation
    },
    showTextNoneIsCorrect() {
      const exceptSlideTypes = ['matchPairs', 'correctOrder']
      if (this.slideActive.type === 'pickAnswer'
        && exceptSlideTypes.includes(this.slideActive.slideType)) return false
      return this.slideActive.showCorrectOption
        && this.slideActive.addCorrectOption
        && !this.checkAnswerCorrect()
        && !this.slideActive.showQuestionImage
        && !this.slideActive.hideResult
        && !this.slideActive.showAcceptedAnswerView
        && (this.slideActive.type === 'multipleChoice' || this.slideActive.type === 'imageChoice'
          || (this.slideActive.type === 'pickAnswer' && this.isQuizResultScreen && this.isPresenting)
        )
    },
    isPollSlide() {
      const slideTypes = ['multipleChoice', 'imageChoice']
      return slideTypes.includes(this.slideActive.type)
    },
    isLeaderboardSlide() {
      return this.slideActive.id && this.slideActive.type === 'leaderboard'
    },
    isOpenEndedSlide() {
      return this.slideActive.id && this.slideActive.type === 'openEnded'
    },
    isWordCloudSlide() {
      return this.slideActive.id && this.slideActive.type === 'wordCloud'
    },
    isQnASlide() {
      return this.slideActive.id && this.slideActive.type === 'question'
    },
    isBrainstormSlide() {
      return this.slideActive.id && this.slideActive.type === 'ideas'
    },
    isScalesSlide() {
      return this.slideActive.id && this.slideActive.type === 'scale'
    },
    isQrCodeSlide() {
      return this.slideActive.id && this.slideActive.type === 'qrcode'
    },
    isSpinnerWheelSlide() {
      return this.slideActive.id && this.slideActive.type === 'spinnerWheel'
    },
    isImageSlide() {
      return this.slideActive.id && this.slideActive.type === 'image'
    },
    isYouTubeSlide() {
      return this.slideActive.id && this.slideActive.type === 'youtube'
    },
    isFreestyleType() {
      return this.slideActive.id && this.slideActive.type === 'freestyle'
    },
    isQuizSlideType() {
      const { id, type } = this.slideActive
      return id && type == 'pickAnswer'
    },
    isPickAnswerSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && !slideType
    },
    isPickImageSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType == 'imageChoice'
    },
    isTypeAnswerSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType === 'typeAnswer'
    },
    isMatchPairsSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType === 'matchPairs'
    },
    isCorrectOrderSlideType() {
      const { slideType } = this.slideActive
      return this.isQuizSlideType && slideType === 'correctOrder'
    },
    isPresentingOrBackstage() {
      return this.isPresenting || this.isBackstage
    },
    isSlideImageInDefaultMode() {
      return this.slideActive.imageType == 'default' && this.slideActive.image
    },
    isGoogleSlidesContentVisible() {
      const { id, type, publishedLink } = this.slideActive
      return id && type == 'googleSlides' && publishedLink
    },
    isCanvasHintsVisible() {
      return this.slideActive.isHintsVisible && !this.slideActive.showQuestionImage
        && !this.presentation.audiencePacing && !this.isSlideActiveHasResult
    },
    isQuizResultScreen() {
      return this.slideActive.quizStatus === QuizStatuses.RESULT_SCREEN
    },
    isSlideActiveHasResult() {
      const { SlideOptions } = this.slideActive
      return SlideOptions && SlideOptions.length && SlideOptions.some((option) => option.count > 0)
    },
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    hideQuestionImage() {
      const { showQuestionImage } = this.slideActive
      this.updateSlideActiveAction({
        showQuestionImage: !showQuestionImage,
      })
      if (this.sharePresentation) {
        return
      }
      this.$emit('updateSlide', {
        accessCode: this.presentation.uniqueAccessCode,
        showQuestionImage: this.slideActive.showQuestionImage,
      }, 4)
    },
    updateQuestionImageLayout(mode) {
      if (this.sharePresentation) {
        return
      }
      this.updateSlideActiveAction({
        questionImageLayout: mode,
      })
      this.$emit('updateSlide', {
        accessCode: this.presentation.uniqueAccessCode,
        questionImageLayout: mode,
      }, 4)
    },
    checkAnswerCorrect() {
      if (this.slideActive.type == 'pickAnswer' && this.slideActive.slideType == 'typeAnswer') {
        if (this.slideActive.correctQuizTypeAnswer) {
          return true
        }
        if (this.slideActive.otherCorrectQuiz && this.slideActive.otherCorrectQuiz.length) {
          return true
        }
      }
      for (
        let i = 0;
        i < this.slideActive.SlideOptions.length;
        i++
      ) {
        if (this.slideActive.SlideOptions[i].correct) {
          return true
        }
      }
      return false
    },

    toggleShowCarousel(type, id) {
      this.$emit('toggleShowCarousel', type, id)
    },

    handleShowWinner(id) {
      this.$emit('handleShowWinner', id)
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-canvas {
  z-index: 200;
  @apply bg-base-0 opacity-0 absolute left-0 top-0 w-full h-full;
}
.audio-control-slide.center-align {
  text-align: center;
  .audio-control {
    -moz-justify-content: center;
    justify-content: center;
  }
}
.audio-control-slide.is-exporting-pdf {
  display: none;
}
.canvas-wrapper {
  height: 100%;
  width: 100%;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  .canvas-content {
    height: 100%;
    width: 100%;
  }
}
.canvas-textSlide {
  width: 82%;
  margin: 0 auto;
}
.canvas-bullet-points {
  width: 100%;
  height: 100%;
}
</style>
